import { text, integer, index } from "drizzle-orm/sqlite-core";
import { defineTable } from "@kubdee/sdk/schema";
import { marketingCampaigns } from "./campaigns";

// ============================================================================
// marketing_content_posts — The post itself (caption, link, schedule, state)
//
// Provider-neutral: a single content post fans out to one or more
// `marketing_post_targets`, each tied to a connector instance. The caption /
// link / media in this row is what the user authored; per-channel transforms
// (e.g. character limits, link previews) happen at publish time inside the
// provider adapter.
//
// Submittable doctype (#644 kind) — `doc_status` follows the standard
// 0=Draft / 1=Submitted / 2=Cancelled triad used everywhere else in the
// platform. Combined with the `status` enum below this gives the calendar /
// inbox views a single boolean (`doc_status === 1`) for "submitted &
// publishable" plus the granular publishing state machine.
// ============================================================================
export const marketingContentPosts = defineTable(
  "marketing",
  "content_posts",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    campaignId: text("campaign_id").references(() => marketingCampaigns.id),
    caption: text("caption").notNull(),
    linkUrl: text("link_url"),
    // Granular publish state machine. See `services/status-transitions.ts`
    // for the allowed transitions.
    status: text("status").notNull().default("draft"),
    scheduledFor: text("scheduled_for"), // ISO datetime, nullable
    // Submit / cancel lifecycle (Doctype Admin convention).
    docStatus: integer("doc_status").notNull().default(0), // 0=Draft, 1=Submitted, 2=Cancelled
    submittedAt: text("submitted_at"),
    submittedBy: text("submitted_by"),
    // Audit columns
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  },
  (table) => [
    index("marketing_content_posts_company_id_idx").on(table.companyId),
    // Calendar query (`?from=...&to=...`) — composite (company_id, status,
    // scheduled_for) lets the planner serve calendar + drafts views without a
    // full scan. Ordering matches the WHERE clause shape used by the route
    // layer (#789).
    index("marketing_content_posts_company_status_scheduled_idx").on(
      table.companyId,
      table.status,
      table.scheduledFor,
    ),
    index("marketing_content_posts_campaign_id_idx").on(table.campaignId),
  ],
);

export type MarketingContentPost = typeof marketingContentPosts.$inferSelect;
export type NewMarketingContentPost = typeof marketingContentPosts.$inferInsert;
